import { PrimitiveAtom, useAtom, useSetAtom } from "jotai";
import React, { useEffect, useMemo, useRef } from "react";
import { useHighlightsApi } from "src/api/merchflows/highlights";
import { baseHighlightsAtom, highlightsModeAtom } from "src/components/Planogram/store/atoms";
import {
  Button,
  Color,
  Flex,
  HorizontalDivider,
  Radios,
  Scroller,
  Select,
  Text,
  Toggle,
} from "src/elements";
import styled from "styled-components";

import type { PlanogramItem } from "@CommonTypes/merchflow/pog/item";
import { Pog } from "@CommonTypes/merchflow/pog/pog";
import { getContainerColor } from "@CommonUtils/pog/container";
import { planogramLegendAtom } from "src/components/PlanogramExtensions/PlanogramLegend";
import {
  labelOptionAtom,
  pinnedLabelOptionsAtom,
  planogramImagesModeAtom,
  showShelfOverflowAtom,
} from "src/components/PlanogramExtensions/store/atoms";
import { getHighlightFieldValue } from "src/components/Planogram/components/Item/store/highlight";
import { savedHighlightAtom } from "src/modals/Highlight/store/atoms";
import { useHighlightModal } from "src/modals/Highlight/store/hooks";
import { EditableHighlight } from "src/modals/Highlight/store/types";
import { isHighlightsSpecialFieldFormula } from "src/modals/Highlight/store/utils";
import { useTranslation } from "src/i18n";
import { Highlight } from "./components/Highlight";

const HighlightsBlock = styled(Flex)`
  flex-direction: column;
  gap: 5px;
  min-height: 0;
`;

const HighlightsContent = styled(Flex)`
  flex-direction: column;
  background-color: ${Color.lightGray};
  border: 1px solid ${Color.lightGrayHover};
  border-radius: 3px;
  min-height: 0;
`;

const NoHighlights = ({ label }: { label: string }) => (
  <Flex width="100%" padding="20px" justify="center" pointerEvents="none">
    <Text variant="body2" color={Color.spaceGray}>
      No {label} Highlights
    </Text>
  </Flex>
);

export const Highlights = ({
  merchflowId,
  pogAtom,
}: {
  merchflowId?: number;
  pogAtom: PrimitiveAtom<Pog>;
}) => {
  const { openHighlightModal } = useHighlightModal();
  const { putBaseHighlightsApi } = useHighlightsApi();
  const { t } = useTranslation("common");

  const [highlightsMode, setHighlightsMode] = useAtom(highlightsModeAtom);
  const [imagesMode, setImagesMode] = useAtom(planogramImagesModeAtom);
  const [baseHighlights, setBaseHighlights] = useAtom(baseHighlightsAtom);
  const [savedHighlight, setSavedHighlight] = useAtom(savedHighlightAtom);
  const [pog] = useAtom(pogAtom);
  const setPlanogramLegend = useSetAtom(planogramLegendAtom);
  const [labelOption, setLabelOption] = useAtom(labelOptionAtom);
  const [pinnedLabelOptions, setPinnedLabelOptions] = useAtom(pinnedLabelOptionsAtom);
  const [showShelfOverflow, setShowShelfOverflow] = useAtom(showShelfOverflowAtom);

  const firstItem = pog.planogram.bays[0]?.shelves.find((shelf) => shelf.items.length > 0)
    ?.items[0];

  const savable = useRef(false);

  useEffect(() => {
    generateLegend();

    if (baseHighlights && savable.current && merchflowId) {
      putBaseHighlightsApi(merchflowId, baseHighlights);
    }

    savable.current = true;
  }, [baseHighlights]);

  useEffect(() => {
    if (!savedHighlight) return;

    const highlightToSave = savedHighlight.highlight;
    if (!highlightToSave) {
      setSavedHighlight(null);
      return;
    }

    if (baseHighlights && savedHighlight.level === "BASE") {
      // Overwrite previous base highlight.
      if (baseHighlights.some((baseHighlight) => baseHighlight.id === highlightToSave.id)) {
        setBaseHighlights((highlights) =>
          (highlights || []).map((highlight) =>
            highlight.id === highlightToSave.id ? highlightToSave : highlight,
          ),
        );

        // New base highlight.
      } else {
        setBaseHighlights((highlights) => [...(highlights || []), highlightToSave]);
      }
    }

    setSavedHighlight(null);
  }, [savedHighlight]);

  const newHighlight = (level: EditableHighlight["level"]) => {
    openHighlightModal({ highlight: null, level });
  };

  const generateLegend = () => {
    if (!baseHighlights) return;

    for (const baseHighlight of baseHighlights.filter(
      (highlight) => highlight.enabled && isHighlightsSpecialFieldFormula(highlight.formula.trim()),
    )) {
      const formulaColorsMap: { [key: string]: string } = {};

      for (const bay of pog.planogram.bays) {
        for (const shelf of bay.shelves) {
          for (const item of shelf.items) {
            const formula = baseHighlight.formula.trim();
            const value = getHighlightFieldValue(formula, item, pog);
            const valueKey = String(value ?? "");

            if (!formulaColorsMap[valueKey]) {
              formulaColorsMap[valueKey] = getContainerColor(
                formula,
                Object.values(formulaColorsMap),
              );
            }
          }
        }
      }

      setPlanogramLegend({ formula: baseHighlight.formula.trim(), formulaColorsMap });
      return;
    }

    setPlanogramLegend({ formula: "", formulaColorsMap: {} });
  };

  const labelOptions = useMemo(() => {
    const noneOption: { label: string; value: "none" } = {
      label: t("cdtPriorityOrder.labelOptions.none"),
      value: "none",
    };

    if (!firstItem) {
      return [noneOption];
    }

    const itemOptions: { label: keyof PlanogramItem; value: keyof PlanogramItem }[] = Object.keys(
      firstItem,
    )
      .filter(
        (key: keyof PlanogramItem) =>
          typeof firstItem[key] === "string" || typeof firstItem[key] === "number",
      )
      .map((key: keyof PlanogramItem) => ({
        label: key,
        value: key,
      }));

    return [noneOption, ...itemOptions];
  }, [firstItem, t]);

  return (
    <>
      <Flex column width="100%" gap="10px" minHeight="0" padding="10px">
        <Flex column gap="10px">
          <Flex width="100%" justify="between">
            <Text>Highlights Enabled</Text>

            <Toggle value={highlightsMode} setValue={setHighlightsMode} />
          </Flex>

          <Flex width="100%" justify="between">
            <Text>Show Shelf Overflow</Text>

            <Toggle value={showShelfOverflow} setValue={setShowShelfOverflow} />
          </Flex>

          <Radios
            label="Mode"
            options={[
              { label: "Image", value: true },
              { label: "Label", value: false },
            ]}
            value={imagesMode}
            setValue={setImagesMode}
          />
          {!imagesMode && (
            <Select<keyof PlanogramItem | "none">
              options={labelOptions}
              value={labelOption}
              setValue={(value) => setLabelOption(value)}
              pinnedValues={pinnedLabelOptions}
              getPinActionLabel={(label, isPinned) =>
                t(
                  isPinned ? "planogramHighlights.unpinLabel" : "planogramHighlights.pinLabelToTop",
                  { label },
                )
              }
              onTogglePin={(option) =>
                setPinnedLabelOptions((pinned) =>
                  pinned.includes(option)
                    ? pinned.filter((pinnedOption) => pinnedOption !== option)
                    : [...pinned, option],
                )
              }
            />
          )}
        </Flex>

        {baseHighlights && (
          <>
            <Flex>
              <HorizontalDivider />
            </Flex>

            <HighlightsBlock>
              <Flex justify="space-between" align="center">
                <Text variant="h5" color={Color.textSecondary}>
                  Base Highlights
                </Text>

                <Button size="small" variant="inverted" onClick={() => newHighlight("BASE")}>
                  New Highlight
                </Button>
              </Flex>

              <Scroller gutter="auto">
                <HighlightsContent>
                  {baseHighlights.length > 0 &&
                    baseHighlights.map((highlight, i) => (
                      <React.Fragment key={i}>
                        {i !== 0 && <HorizontalDivider color={Color.lightGrayHover} />}

                        <Highlight
                          highlight={highlight}
                          level="BASE"
                          highlightsAtom={baseHighlightsAtom}
                        />
                      </React.Fragment>
                    ))}

                  {baseHighlights.length === 0 && <NoHighlights label="Base" />}
                </HighlightsContent>
              </Scroller>
            </HighlightsBlock>
          </>
        )}
      </Flex>
    </>
  );
};
